import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { Eip1193Provider } from "../wallet/eip1193";
import { matchesTransactionScope, TransactionTracker, transactionUnresolved,
  type TransactionScope } from "../wallet/transaction-tracker";

export function useTransactions(scope: TransactionScope | null, walletChainId: number | null,
  provider: Eip1193Provider | null, onConfirmed: () => void) {
  const [tracker] = useState(() => new TransactionTracker(sessionStorage));
  const snapshot = useSyncExternalStore(tracker.subscribe, tracker.getSnapshot, tracker.getSnapshot);
  const account = scope?.account;
  const chainId = scope?.chainId;
  const collection = scope?.collection;
  const environment = scope?.environment;
  const stableScope = useMemo(() => account && chainId && collection && environment ?
    { account, chainId, collection, environment } : null, [account, chainId, collection, environment]);
  const seenResults = useRef(new Set<string>());

  useEffect(() => {
    if (!provider || !stableScope || walletChainId !== stableScope.chainId) return;
    let stopped = false;
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const attempts = new Map<string, number>();
    const check = (id: string) => tracker.check(id, provider, stableScope).catch(() => {
      // Unknown results stay locked; a read failure never authorizes another send.
      console.error("Transaction recovery watcher could not be started.");
    });
    const schedule = () => {
      if (stopped) return;
      const pending = tracker.getSnapshot().items.filter(item =>
        matchesTransactionScope(item, stableScope) && item.hash && transactionUnresolved(item));
      for (const [id, timer] of timers) {
        if (!pending.some(item => item.id === id && item.phase === "UNCONFIRMED")) {
          clearTimeout(timer); timers.delete(id);
        }
      }
      for (const id of attempts.keys()) if (!pending.some(item => item.id === id)) attempts.delete(id);
      for (const item of pending) {
        if (item.phase !== "UNCONFIRMED" || timers.has(item.id)) continue;
        const attempt = attempts.get(item.id) ?? 0;
        // Retry observations only, 5/10/20/40/60 seconds after failure. The
        // tracker coalesces manual/automatic checks and verifies canonical receipts.
        timers.set(item.id, setTimeout(() => {
          timers.delete(item.id);
          if (stopped) return;
          attempts.set(item.id, Math.min(attempt + 1, 4));
          check(item.id).finally(schedule).catch(() => {});
        }, Math.min(5_000 * 2 ** attempt, 60_000)));
      }
    };
    const unsubscribe = tracker.subscribe(schedule);
    // Restore existing hashes once; subsequent timeout/RPC failures retry above.
    for (const item of tracker.getSnapshot().items) {
      if (matchesTransactionScope(item, stableScope) && item.hash && transactionUnresolved(item)) {
        check(item.id).finally(schedule).catch(() => {});
      }
    }
    schedule();
    return () => {
      stopped = true; unsubscribe();
      for (const timer of timers.values()) clearTimeout(timer);
      tracker.stopWatching();
    };
  }, [tracker, stableScope, provider, walletChainId]);

  useEffect(() => {
    let changed = false;
    for (const item of snapshot.items) {
      const resultKey = `${item.id}:${item.phase}:${item.receiptBlockHash ?? ""}`;
      if (stableScope && matchesTransactionScope(item, stableScope) &&
        ["CONFIRMED", "REVERTED", "ORPHANED", "SAMPLING_REQUIRED", "SAMPLING_EXHAUSTED", "NOT_NEEDED"].includes(item.phase) && !seenResults.current.has(resultKey)) {
        seenResults.current.add(resultKey);
        changed = true;
      }
    }
    if (changed) onConfirmed();
  }, [snapshot.items, stableScope, onConfirmed]);

  const checkTransaction = useCallback(async (id: string) => {
    if (!provider || !stableScope) throw new Error("Connect the transaction owner wallet first");
    await tracker.check(id, provider, stableScope);
  }, [tracker, provider, stableScope]);
  const dismissTransaction = useCallback((id: string) => { tracker.dismiss(id); }, [tracker]);
  const items = stableScope ? snapshot.items.filter((item) => matchesTransactionScope(item, stableScope)) : [];
  return {
    tracker,
    view: { items, redemptions: stableScope ? snapshot.redemptions.filter(item => matchesTransactionScope(item, stableScope)) : [],
      socialTaskClaimed: stableScope ? tracker.socialTaskClaimed(stableScope) : false,
      socialClaimStorageWarning: snapshot.socialClaimStorageWarning,
      referralClaims: stableScope ? snapshot.referralClaims.filter(claim => matchesTransactionScope(claim, stableScope)) : [],
      referralClaimStorageWarning: snapshot.referralClaimStorageWarning,
      storageWarning: snapshot.storageWarning, blocked: items.some(transactionUnresolved) },
    checkTransaction,
    dismissTransaction,
  };
}
